import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "react-router-dom";
import { formatNOK, formatDate } from "@/lib/format";
import { OfferProbabilityBadge } from "./OfferProbabilityBadge";
import type { OfferWithCustomer } from "@/hooks/use-offers";
import { cn } from "@/lib/utils";

interface Props {
  offer: OfferWithCustomer;
  /** Deaktiverer drag mens en statusendring (f.eks. lost-bekreftelse) pågår. */
  disabled?: boolean;
}

/**
 * VIB-1304: draggable kort i kanban-boardet. Viser kunde, beløp, kategori,
 * gyldig-til og selger — jf. spec. Klikk (uten drag) navigerer til editoren
 * via en overlay-lenke (dnd-kit fanger opp mousedown/pointerdown, så et
 * eksplisitt "åpne"-ikon unngår konflikt mellom drag og klikk-navigasjon).
 */
export function OfferBoardCard({ offer, disabled }: Props) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: offer.id,
    disabled,
  });

  const style = transform
    ? { transform: CSS.Translate.toString(transform) }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={cn(
        "border rounded-md bg-background p-2.5 shadow-sm cursor-grab active:cursor-grabbing touch-none select-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
        isDragging && "opacity-50 z-10",
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <Link
          to={`/tilbud/${offer.id}`}
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-sm truncate hover:underline"
        >
          {offer.title}
        </Link>
      </div>
      <p className="text-xs text-muted-foreground truncate mb-1.5">
        {offer.customer?.name ?? offer.customer_name ?? "—"}
      </p>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-sm font-semibold tabular-nums">{formatNOK(offer.total_amount)}</span>
        <OfferProbabilityBadge probability={offer.probability} />
      </div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{offer.valid_until ? `Gyldig til ${formatDate(offer.valid_until)}` : "—"}</span>
        <span className="truncate max-w-[100px]">{offer.creator?.full_name ?? "—"}</span>
      </div>

      {/* VIB-1304 (Erik 15.07, smoke-test-fiks A4): begrunnelsen samlet inn i
          LostReasonDialog må vises i Tapt-bøtta, jf. spec "vises på tilbudet
          og i «Tapt»-bøtta på boardet". */}
      {offer.status === "lost" && offer.lost_reason && (
        <p className="mt-1.5 text-[11px] text-destructive line-clamp-2">{offer.lost_reason}</p>
      )}
    </div>
  );
}
